import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API,
});

// Request interceptor - add token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('lousway_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Export api instance for use in other files
export { api };

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage immediately
    const stored = localStorage.getItem('lousway_user');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('lousway_token');
  });

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('lousway_token');

    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
      localStorage.setItem('lousway_user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Auth check failed:', error);
      // Only clear if token is actually invalid (401)
      if (error.response?.status === 401) {
        localStorage.removeItem('lousway_user');
        localStorage.removeItem('lousway_token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithCredentials = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });

    if (response.data?.token) {
      localStorage.setItem('lousway_token', response.data.token);
      localStorage.setItem('lousway_user', JSON.stringify(response.data));
      setUser(response.data);
      setIsAuthenticated(true);
    }

    return response.data;
  };

  const register = async (data) => {
    const response = await api.post('/auth/register', data);

    if (response.data?.token) {
      localStorage.setItem('lousway_token', response.data.token);
      localStorage.setItem('lousway_user', JSON.stringify(response.data));
      setUser(response.data);
      setIsAuthenticated(true);
    }

    return response.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('lousway_user');
      localStorage.removeItem('lousway_token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const processSession = async (sessionId) => {
    try {
      const response = await api.post('/auth/session', { session_id: sessionId });

      if (response.data?.token) {
        localStorage.setItem('lousway_token', response.data.token);
        localStorage.setItem('lousway_user', JSON.stringify(response.data));
        setUser(response.data);
        setIsAuthenticated(true);
      }

      return response.data;
    } catch (error) {
      console.error('Session processing error:', error);
      throw error;
    }
  };

  const updateProfile = async (data) => {
    const response = await api.put('/auth/profile', data);
    setUser(response.data);
    localStorage.setItem('lousway_user', JSON.stringify(response.data));
    return response.data;
  };

  const requestPasswordReset = async (email) => {
    // Normaliza apenas o email enviado para recuperar acesso.
    return await api.post('/auth/forgot-password', { email: String(email || '').trim().toLowerCase() });
  };

  const resetPassword = async (token, newPassword) => {
    // Esta rota tem de corresponder à rota de confirmação em server.py.
    return await api.post('/auth/reset-password-confirm', { token, new_password: newPassword });
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
    localStorage.setItem('user', JSON.stringify({ ...user, ...userData }));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login: loginWithGoogle,
        loginWithCredentials,
        register,
        logout,
        processSession,
        updateProfile,
        updateUser,
        checkAuth,
        requestPasswordReset,
        resetPassword,
        api,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
